import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, ShoppingCart, Menu, X, LogOut, LogIn } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = [
  { to: "/", label: "Start" },
  { to: "/cosplay", label: "Cosplay" },
  { to: "/3d-druck", label: "3D-Druck auf Anfrage" },
  { to: "/produkte", label: "Fertige Produkte" },
];

const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const linkCls = ({ isActive }) =>
    `relative px-1 py-2 text-[15px] font-semibold transition-colors duration-200 ${
      isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
    } after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[3px] after:rounded-full after:transition-all after:duration-300 ${
      isActive
        ? "after:bg-gradient-to-r after:from-[#1b9ad6] after:to-[#7cc24a] after:opacity-100"
        : "after:opacity-0 hover:after:opacity-60 hover:after:bg-gradient-to-r hover:after:from-[#1b9ad6] hover:after:to-[#7cc24a]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100 shadow-[0_2px_16px_rgba(27,154,214,0.06)]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8">
        <div className="flex h-[76px] items-center justify-between gap-2 sm:gap-4">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkCls}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Suchlupe: direkte Verknüpfung zu Fertige Produkte */}
            <Link
              to="/produkte"
              aria-label="Suche – zu den Produkten"
              className="p-2 sm:p-2.5 rounded-full text-slate-700 hover:bg-sky-50 hover:text-[#1b9ad6] transition-colors duration-200"
            >
              <Search className="h-[22px] w-[22px]" strokeWidth={2.2} />
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label="Konto"
                  className={`p-2 sm:p-2.5 rounded-full transition-colors duration-200 ${
                    user
                      ? "text-[#1b9ad6] bg-sky-50"
                      : "text-slate-700 hover:bg-sky-50 hover:text-[#1b9ad6]"
                  }`}
                >
                  <User className="h-[22px] w-[22px]" strokeWidth={2.2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                {user ? (
                  <>
                    <DropdownMenuLabel className="font-display">
                      Hallo, <span className="text-[#1b9ad6]">{user.name}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/profil")}>
                      <User className="h-4 w-4 mr-2" /> Mein Profil
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer text-red-500 focus:text-red-600"
                      onClick={() => {
                        logout();
                        navigate("/");
                      }}
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Abmelden
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/login")}>
                    <LogIn className="h-4 w-4 mr-2" /> Anmelden
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              aria-label="Warenkorb"
              onClick={() => setOpen(true)}
              className="relative p-2 sm:p-2.5 rounded-full text-slate-700 hover:bg-sky-50 hover:text-[#1b9ad6] transition-colors duration-200"
            >
              <ShoppingCart className="h-[22px] w-[22px]" strokeWidth={2.2} />
              <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1b9ad6] px-1 text-[11px] font-bold text-white">
                {count}
              </span>
            </button>

            <button
              aria-label="Menü"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 sm:p-2.5 rounded-full text-slate-700 hover:bg-sky-50 transition-colors duration-200"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden border-t border-slate-100 bg-white px-6 py-4 flex flex-col gap-1 animate-accordion-down">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `rounded-xl px-4 py-3 font-semibold transition-colors duration-200 ${
                  isActive
                    ? "bg-gradient-to-r from-sky-50 to-lime-50 text-[#1b7fc1]"
                    : "text-slate-600 hover:bg-slate-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
